import React, { useCallback, useState } from "react";
import { COLOR_PRIMARY } from "../../styles/colors";
import useGame from "../../hooks/useGame";
import Typography from "@mui/material/Typography";
import GameTimer from "../gameTimer/GameTimer";
import useMediaQuery from "@mui/material/useMediaQuery";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { setIopsConfigModalOpen } from "../../store/game/gameSlice";
import {
  selectInitialRootIOPS,
  selectRootBlockIOPS,
  selectTrafficPattern,
} from "../../store/game/selectors";
import { OperationTypes } from "../../models/block";
import Box from "@mui/material/Box";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { getCurrentDesignName, setCurrentDesignName } from "../../store";
import IOPSDisplay from "./IOPSDisplay";
import ScoreBoardActions from "./ScoreBoardActions";
import IOPSConfigModal from "../game/IOPSConfigModal";
import { useReactFlow } from "reactflow";

const ScoreBoard: React.FC = () => {
  const [gameState, startGame, pauseGame, resumeGame, resetGame, softResetGame] = useGame();
  const { setViewport } = useReactFlow();
  const isMobile = useMediaQuery(`(max-width: 550px)`);
  const isDesktop = useMediaQuery(`(min-width: 950px)`);
  const showOneRow = useMediaQuery(`(min-width: 730px)`);
  const [designName, setDesignName] = useState<string | null>(
    getCurrentDesignName(),
  );
  const dispatch = useAppDispatch();
  const isSettingsModalOpen = useAppSelector((state) => state.game.isIopsConfigModalOpen || false);

  const initialIOPS = useAppSelector((state) =>
    selectInitialRootIOPS(state.game),
  );
  const currentRootIOPS = useAppSelector((state) =>
    selectRootBlockIOPS(state.game),
  );
  const trafficPattern = useAppSelector((state) =>
    selectTrafficPattern(state.game),
  );

  const iopsToDisplay =
    gameState.isStarted && currentRootIOPS ? currentRootIOPS : initialIOPS;

  const totalIOPS = iopsToDisplay?.total || 0;
  const readTotal =
    iopsToDisplay?.byOperationType[OperationTypes.READ]?.total || 0;
  const readPct = totalIOPS > 0 ? Math.round((readTotal / totalIOPS) * 100) : 0;
  const writePct = 100 - readPct;

  const handleStartPauseClick = useCallback(() => {
    if (!gameState.isStarted) {
      trackEvent(GA_EVENTS.GAME_STARTED);
      startGame();
    } else if (gameState.isRunning) {
      trackEvent(GA_EVENTS.GAME_PAUSED);
      pauseGame();
    } else {
      trackEvent(GA_EVENTS.GAME_RESUMED);
      resumeGame();
    }
  }, [gameState, pauseGame, startGame, resumeGame]);

  const handleSettingsClick = useCallback(() => {
    if (gameState.isRunning) {
      trackEvent(GA_EVENTS.GAME_PAUSED);
      pauseGame();
    }
    trackEvent(GA_EVENTS.IOPS_MODAL_OPENED);
    dispatch(setIopsConfigModalOpen(true));
  }, [gameState.isRunning, pauseGame, dispatch]);

  const handleResetConfirm = useCallback(() => {
    trackEvent("simulation_reset");
    softResetGame();
  }, [softResetGame]);

  const handleNewDesignConfirm = useCallback(() => {
    trackEvent(GA_EVENTS.CANVAS_RESET);
    setCurrentDesignName(null);
    setDesignName(null);
    resetGame();
    setViewport({ x: 0, y: 0, zoom: 1 });
  }, [resetGame, setViewport]);

  return (
    <Card
      style={{
        position: "absolute",
        top: isMobile ? '10px' : "10px",
        left: isMobile ? '10px' : "10px",
        right: isMobile ? '10px' : "10px",
        display: "flex",
        flexDirection: !showOneRow ? "column" : "row",
        justifyContent: "space-between",
        gap: isMobile ? "4px" : "10px",
        zIndex: 100,
        padding: isMobile ? "10px 15px" : "5px 16px",
        alignItems: !showOneRow ? "stretch" : "center",
      }}
      elevation={5}
    >
      {/* Row 1: Logo + Design name + IOPS display */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minWidth: 0,
          }}
        >
          <img
            src="/logo100.png"
            alt="System Design Playground Logo"
            style={{
              width: isMobile ? "24px" : "32px",
              height: isMobile ? "24px" : "32px",
              objectFit: "contain",
              filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
              flexShrink: 0,
            }}
          />
          {(isDesktop) && (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: COLOR_PRIMARY,
                letterSpacing: "-0.3px",
                whiteSpace: "nowrap",
              }}
            >
              System Design Playground
            </Typography>
          )}
          {/* Design Name Label */}
          <Chip
            label={designName || "Scratchpad"}
            size="small"
            variant={designName ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              color: designName ? "white" : COLOR_PRIMARY,
              backgroundColor: designName ? COLOR_PRIMARY : "transparent",
              borderColor: COLOR_PRIMARY,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        </Box>

        {(!showOneRow) && (
          <IOPSDisplay
            id="scoreboard-iops-display"
            totalIOPS={totalIOPS}
            readPct={readPct}
            writePct={writePct}
            trafficPattern={trafficPattern}
            isMobile={isMobile}
            gameIsRunning={gameState.isRunning}
            onClick={handleSettingsClick}
          />
        )}
      </Box>

      {/* Row 2: Timer + Action buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: isMobile ? "4px" : "10px",
          flexShrink: 0,
          justifyContent: isMobile ? "space-between" : "flex-end",
        }}
      >
        <Typography
          variant="h2"
          style={{
            color: COLOR_PRIMARY,
            fontSize: isMobile ? "22px" : "32px",
            fontWeight: 800,
            display: "none", // Hidden for now
          }}
        >
          Lv.{gameState.level}
        </Typography>
        <Typography
          variant="h2"
          style={{
            color: "#077620",
            fontSize: isMobile ? "22px" : "32px",
            fontWeight: 800,
            display: "none", // Hidden for now
          }}
        >
          ${gameState.accountBalance.toFixed(2)}
        </Typography>
        {showOneRow && (
          <IOPSDisplay
            id="scoreboard-iops-display"
            totalIOPS={totalIOPS}
            readPct={readPct}
            writePct={writePct}
            trafficPattern={trafficPattern}
            isMobile={isMobile}
            gameIsRunning={gameState.isRunning}
            onClick={handleSettingsClick}
          />
        )}

        <GameTimer
          elapsedTime={gameState.timeElapsedInSec}
          isRunning={gameState.isRunning}
          isStarted={gameState.isStarted}
        />


        <ScoreBoardActions
          isMobile={isMobile}
          isRunning={gameState.isRunning}
          isStarted={gameState.isStarted}
          designName={designName}
          onPlayPauseClick={handleStartPauseClick}
          onResetConfirm={handleResetConfirm}
          onNewDesignConfirm={handleNewDesignConfirm}
          onDesignSaved={(name) => setDesignName(name)}
          onDesignLoaded={(name) => setDesignName(name)}
        />
      </Box>

      {/* Settings / IOPS Config Modal */}
      <IOPSConfigModal
        open={isSettingsModalOpen}
        onClose={() => dispatch(setIopsConfigModalOpen(false))}
      />
    </Card>
  );
};

export default ScoreBoard;
